import { useCallback, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiPlus } from 'react-icons/fi';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { AppList } from '@/shared/app-list-layout';
import type { DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import type { BadgeTone } from '@/shared/ui/badge';
import { CatalogApi } from '@/modules/catalog/services/api';
import type { CanonicalProduct, PublicationState } from '@/api/schemas/catalog';

/**
 * The catalogue list.
 *
 * Reads the shared catalogue (M66, public scope) and lists it through the shared
 * `AppList` surface, so it carries the same tabs, search, filters, pager and
 * table/cards toggle as every other list in the console.
 *
 * The catalogue read takes no query parameters, so the narrowing happens locally
 * over the collection the read returned.
 */

/** Status options surfaced in the column-bound filter dropdown. */
const STATE_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'retired', label: 'Retired' },
  { value: 'merged', label: 'Merged' },
];

/** Kind options surfaced in the column-bound filter dropdown. */
const KIND_OPTIONS = [
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
];

const PUBLICATION_TONE: Record<PublicationState, { tone: BadgeTone; label: string }> = {
  published: { tone: 'positive', label: 'Published' },
  draft: { tone: 'neutral', label: 'Draft' },
  retired: { tone: 'caution', label: 'Retired' },
  merged: { tone: 'neutral', label: 'Merged' },
};

/** Saved views rendered as tabs above the catalogue. */
function buildProductViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All products', count: counts.all },
    {
      id: 'published',
      label: 'Published',
      filters: { publicationState: 'published' },
      count: counts.published,
    },
    { id: 'draft', label: 'Draft', filters: { publicationState: 'draft' }, count: counts.draft },
    {
      id: 'retired',
      label: 'Retired',
      filters: { publicationState: 'retired' },
      count: counts.retired,
    },
  ];
}

/**
 * The fields a search term is matched against.
 *
 * The state is matched by its LABEL, not its wire value: a user searching for
 * "published" is looking for the word they can see on the row, and the row shows
 * "Published" rather than `published`.
 */
function searchFields(row: CanonicalProduct): ReadonlyArray<string | null | undefined> {
  return [row.name, row.modelCode, PUBLICATION_TONE[row.publicationState].label];
}

const COLUMNS: ReadonlyArray<ListColumn<CanonicalProduct>> = [
  {
    id: 'name',
    header: 'Product',
    primary: true,
    sortable: true,
    cell: (row) => (
      <div className="flex flex-col">
        <span className="font-medium text-content">{row.name}</span>
        <span className="text-xs text-content-muted">{row.modelCode ?? '—'}</span>
      </div>
    ),
  },
  {
    id: 'kind',
    header: 'Kind',
    sortable: true,
    filter: { type: 'select', options: KIND_OPTIONS, placeholder: 'Any' },
    cell: (row) => <span className="capitalize">{row.kind}</span>,
  },
  {
    id: 'variants',
    header: 'Variants',
    cell: (row) => <span>{row.variants.length}</span>,
  },
  {
    id: 'publicationState',
    header: 'State',
    sortable: true,
    filter: { type: 'select', options: STATE_OPTIONS, placeholder: 'Any' },
    cell: (row) => {
      const { tone, label } = PUBLICATION_TONE[row.publicationState];
      return <Badge tone={tone}>{label}</Badge>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    cell: (row) => (
      <span className="whitespace-nowrap">
        {new Date(row.updatedAt).toLocaleDateString()}
        <span className="ml-1.5 text-content-muted">
          {new Date(row.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    ),
  },
];

export function ListPage(): ReactNode {
  const navigate = useNavigate();
  const read = useCallback(() => CatalogApi.listProducts(), []);

  return (
    <AppList<CanonicalProduct>
      title="Products"
      description="The catalogue every tenant draws its sellable offers from."
      read={read}
      searchFields={searchFields}
      countKeyOf={(row) => row.publicationState}
      buildViews={buildProductViews}
      columns={COLUMNS}
      getRowId={(row) => row.id}
      rowActions={[
        {
          id: 'view',
          label: 'View',
          // The ROUTER navigates, not the browser: a full page load would tear
          // the session down and rebuild it, for a link inside the console.
          onSelect: (row) => {
            void navigate({ to: '/catalog/products/$productId', params: { productId: row.id } });
          },
        },
      ]}
      // Six columns run off a phone screen, so the page asks for the responsive
      // presentation: cards below `sm`, the table above.
      responsive
      toolbarActions={
        <Button
          size="sm"
          aria-label="New product"
          onClick={() => {
            void navigate({ to: '/catalog/setup' });
          }}
        >
          <FiPlus size={14} aria-hidden="true" />
          <span className="hidden sm:inline">New product</span>
        </Button>
      }
      emptyTitle="The catalogue is empty"
      emptyDescription="No canonical product has been published on this deployment yet."
      noMatchDescription="No product matches this tab, search or filter."
    />
  );
}
